import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PasswordInput from "@/components/shared/PasswordInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  UserPlus, KeyRound, ShieldAlert, ShieldCheck, UserCog, ArrowLeft,
  Loader2, ScrollText, Lock, Unlock, Sparkles, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";

// Generate a readable but secure 12-char password.
// Excludes confusable characters (0/O, 1/l/I).
function generatePassword(len = 12) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digit = "23456789";
  const symbol = "!@#$%&*";
  const all = upper + lower + digit + symbol;
  // Guarantee at least one of each class
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const chars = [pick(upper), pick(lower), pick(digit), pick(symbol)];
  for (let i = chars.length; i < len; i++) chars.push(pick(all));
  // Fisher–Yates shuffle
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

const ROLE_OPTIONS = ["ADMIN", "FOH", "TECH"];

/**
 * Admin → Staff management page.
 *
 * Live-renders the user table, supports creating users, resetting passwords,
 * disabling / re-enabling, changing role, and viewing the admin audit log.
 *
 * Routes mounted at /app/admin/staff (allowedRoles=["ADMIN"]).
 */
export default function AdminStaffPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  };
  const loadAudit = async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-log?limit=100`);
      setAudit(res.data.entries || []);
    } catch {
      /* silent */
    }
  };
  useEffect(() => {
    loadUsers();
    loadAudit();
  }, []);

  // --- Actions -------------------------------------------------------------

  const toggleDisabled = async (u) => {
    const next = !u.disabled;
    const label = next ? "disable" : "re-enable";
    if (!window.confirm(`${next ? "Disable" : "Re-enable"} ${u.email}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/toggle-disabled`, { disabled: next });
      toast.success(`Account ${label}d`);
      loadUsers();
      loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${label} account`);
    }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === (u.role || "").toUpperCase()) return;
    try {
      await axios.patch(`${API}/admin/users/${u.id}/role`, { role: newRole });
      toast.success(`Role changed to ${newRole}`);
      loadUsers();
      loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change role");
    }
  };

  // ------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-500 hover:text-slate-800 inline-flex items-center text-[12.5px] mb-4"
          data-testid="admin-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </button>

        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">
              Admin
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">Staff & permissions</h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Manage who can log in and what they can do.
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="admin-add-staff-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add staff member
          </Button>
        </div>

        <Tabs defaultValue="staff" className="w-full">
          <TabsList>
            <TabsTrigger value="staff" data-testid="admin-tab-staff">
              Staff ({users.length})
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">
              <ScrollText className="w-3.5 h-3.5 mr-1.5" />
              Activity log
            </TabsTrigger>
          </TabsList>

          {/* Staff tab */}
          <TabsContent value="staff" className="pt-4">
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              {loading ? (
                <div className="px-6 py-10 text-center text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                  Loading staff…
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="admin-staff-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left px-4 py-2.5 font-medium">Name</th>
                      <th className="text-left px-4 py-2.5 font-medium">Email</th>
                      <th className="text-left px-4 py-2.5 font-medium">Role</th>
                      <th className="text-left px-4 py-2.5 font-medium">Last login</th>
                      <th className="text-right px-4 py-2.5 font-medium">Jobs</th>
                      <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isMe = u.id === me?.id;
                      const isDisabled = !!u.disabled;
                      return (
                        <tr
                          key={u.id}
                          className={`border-b border-slate-100 last:border-0 ${
                            isDisabled ? "bg-slate-50/60 text-slate-400" : ""
                          }`}
                          data-testid={`staff-row-${u.email}`}
                        >
                          <td className="px-4 py-3">
                            <span className="font-medium">{u.name || "—"}</span>
                            {isMe && (
                              <Badge className="ml-2 bg-blue-50 text-blue-700 border border-blue-200" variant="outline">
                                You
                              </Badge>
                            )}
                            {isDisabled && (
                              <Badge className="ml-2 bg-slate-100 text-slate-500 border border-slate-200" variant="outline">
                                Disabled
                              </Badge>
                            )}
                            {u.must_change_password && (
                              <Badge className="ml-2 bg-amber-50 text-amber-700 border border-amber-200" variant="outline">
                                Reset pending
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{u.email}</td>
                          <td className="px-4 py-3">
                            <select
                              value={(u.role || "").toUpperCase()}
                              disabled={isMe}
                              onChange={(e) => changeRole(u, e.target.value)}
                              className="text-[12.5px] border border-slate-200 rounded px-2 py-1 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
                              data-testid={`role-select-${u.email}`}
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-500">
                            {u.last_login_at ? formatLastLogin(u.last_login_at) : "Never"}
                          </td>
                          <td className="px-4 py-3 text-right text-[12.5px] text-slate-600 font-mono">
                            {u.jobs_handled || 0}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setResetUser(u)}
                                className="text-[11.5px] h-7"
                                data-testid={`reset-pwd-${u.email}`}
                              >
                                <KeyRound className="w-3 h-3 mr-1" /> Reset
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isMe}
                                onClick={() => toggleDisabled(u)}
                                className={`text-[11.5px] h-7 ${
                                  isDisabled
                                    ? "text-teal-700 border-teal-200 hover:bg-teal-50"
                                    : "text-red-600 border-red-200 hover:bg-red-50"
                                }`}
                                data-testid={`toggle-disabled-${u.email}`}
                              >
                                {isDisabled ? (
                                  <>
                                    <Unlock className="w-3 h-3 mr-1" /> Re-enable
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3 mr-1" /> Disable
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                          No staff yet. Add the first one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-[11.5px] text-slate-400 mt-3 italic">
              Disabled users cannot log in. Their job history and audit trail remain intact.
            </p>
          </TabsContent>

          {/* Audit tab */}
          <TabsContent value="audit" className="pt-4">
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              {audit.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-500">
                  No admin actions logged yet.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="admin-audit-list">
                  {audit.map((e, i) => (
                    <li key={i} className="px-4 py-2.5 flex items-center gap-3 text-[12.5px]">
                      <ActionIcon action={e.action} />
                      <span className="text-slate-400 font-mono text-[11px] w-36">
                        {(e.created_at || "").slice(0, 19).replace("T", " ")}
                      </span>
                      <span className="text-slate-700 flex-1">
                        <span className="font-medium">{e.actor_email}</span>
                        <span className="text-slate-400 mx-1.5">·</span>
                        <span className="lowercase">{e.action.replace(/_/g, " ")}</span>
                        {e.target_email && (
                          <>
                            <span className="text-slate-400 mx-1.5">→</span>
                            <span className="font-medium">{e.target_email}</span>
                          </>
                        )}
                      </span>
                      {e.details?.from && e.details?.to && (
                        <span className="text-[11px] text-slate-500 font-mono">
                          {e.details.from} → {e.details.to}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <CreateStaffDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          loadUsers();
          loadAudit();
        }}
      />
      <ResetPasswordDialog
        user={resetUser}
        onClose={() => setResetUser(null)}
        onDone={() => {
          setResetUser(null);
          loadUsers();
          loadAudit();
        }}
      />
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

function CreateStaffDialog({ open, onClose, onCreated }) {
  const [draft, setDraft] = useState({ email: "", name: "", role: "FOH", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // {email, password} after save

  useEffect(() => {
    if (open) {
      setDraft({ email: "", name: "", role: "FOH", password: "" });
      setCreated(null);
    }
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, draft);
      // Surface the password to the admin once — they need to share it with the user.
      // Server stores only the bcrypt hash; this is the last time it can be displayed.
      setCreated({ email: draft.email, password: draft.password });
      toast.success(`${draft.email} added — share the password below`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-create-dialog">
        <DialogHeader>
          <DialogTitle>{created ? "Staff member added" : "Add staff member"}</DialogTitle>
        </DialogHeader>

        {created ? (
          <RevealPasswordCard
            email={created.email}
            password={created.password}
            note="They'll be prompted to change this on first login. This password will not be shown again."
          />
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Name</p>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                autoFocus
                data-testid="create-name-input"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Email</p>
              <Input
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                data-testid="create-email-input"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Role</p>
              <select
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm bg-white"
                data-testid="create-role-select"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">
                  Initial password
                </p>
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, password: generatePassword() })}
                  className="text-[11px] text-blue-700 hover:text-blue-900 inline-flex items-center"
                  data-testid="create-generate-pwd"
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Generate
                </button>
              </div>
              <PasswordInput
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                placeholder="Min 8 chars"
                testId="create-password-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                The user will be prompted to change this on first login.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {created ? (
            <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-done-btn">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={
                  submitting || !draft.name || !draft.email || (draft.password || "").length < 8
                }
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="create-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Add staff
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reset, setReset] = useState(null); // {email, password} after save
  useEffect(() => {
    setPwd("");
    setReset(null);
  }, [user]);

  if (!user) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users/${user.id}/reset-password`, { new_password: pwd });
      setReset({ email: user.email, password: pwd });
      toast.success(`Password reset for ${user.email} — share it below`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-reset-dialog">
        <DialogHeader>
          <DialogTitle>
            {reset ? `Password reset — ${user.email}` : `Reset password — ${user.email}`}
          </DialogTitle>
        </DialogHeader>

        {reset ? (
          <RevealPasswordCard
            email={reset.email}
            password={reset.password}
            note="The user will be required to change this on their next login. This password will not be shown again."
          />
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600 text-[12.5px]">
              Choose a temporary password to give to the user. They will be required to change it on
              their next login.
            </p>
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">New password</p>
              <button
                type="button"
                onClick={() => setPwd(generatePassword())}
                className="text-[11px] text-blue-700 hover:text-blue-900 inline-flex items-center"
                data-testid="reset-generate-pwd"
              >
                <Sparkles className="w-3 h-3 mr-1" /> Generate
              </button>
            </div>
            <PasswordInput
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Min 8 chars"
              autoFocus
              testId="reset-pwd-input"
            />
          </div>
        )}

        <DialogFooter>
          {reset ? (
            <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="reset-done-btn">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || pwd.length < 8}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="reset-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Reset password
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Reveal-after-save card — shown once so the admin can hand the password
// to the staff member. The server stores only the bcrypt hash; this card
// is the LAST time the plaintext can be displayed.
// ---------------------------------------------------------------------------

function RevealPasswordCard({ email, password, note }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="space-y-3" data-testid="reveal-password-card">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-medium mb-1">
          Share these credentials with {email}
        </p>
        <p className="text-[11px] text-emerald-900/70 mb-3">{note}</p>
        <div className="flex items-center justify-between gap-2 bg-white border border-emerald-200 rounded px-3 py-2">
          <code className="text-[15px] font-mono text-slate-900 select-all" data-testid="revealed-password">
            {password}
          </code>
          <button
            type="button"
            onClick={copy}
            className="text-[11px] text-emerald-700 hover:text-emerald-900 inline-flex items-center"
            data-testid="copy-password-btn"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 mr-1" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionIcon({ action }) {
  const map = {
    create_user: <UserPlus className="w-3.5 h-3.5 text-emerald-600" />,
    reset_password: <KeyRound className="w-3.5 h-3.5 text-amber-600" />,
    disable_user: <ShieldAlert className="w-3.5 h-3.5 text-red-600" />,
    enable_user: <ShieldCheck className="w-3.5 h-3.5 text-teal-600" />,
    change_role: <UserCog className="w-3.5 h-3.5 text-blue-600" />,
  };
  return map[action] || <ScrollText className="w-3.5 h-3.5 text-slate-400" />;
}

function formatLastLogin(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
